import React, { useState, useMemo } from "react";
import {
  CreditCard,
  Mail,
  DollarSign,
  Send,
  CheckCircle,
  Clock,
  ChevronDown,
} from "lucide-react";
import { formatCurrency } from "../utils/helpers";
import Card from "./ui/Card";
import Button from "./ui/Button";

// Helper to safely parse numbers
const parseNum = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

// Helper to validate date value
const isValidDate = (d) => {
  const date = new Date(d);
  return date instanceof Date && !isNaN(date);
};

const PayoutRequest = ({
  affiliateName,
  processedAffiliateData = [],
  onSubmitRequest,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState("");
  const [selectedWeeks, setSelectedWeeks] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const weeklyEarnings = useMemo(() => {
    const weeklyData = new Map();

    processedAffiliateData.forEach((ad) => {
      if (!ad.Date || !isValidDate(ad.Date)) return;
      const adDate = new Date(ad.Date);

      const dayOfWeek = adDate.getDay();
      const weekStart = new Date(adDate);
      weekStart.setDate(
        adDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)
      );
      const weekKey = weekStart.toISOString().split("T")[0];

      if (!weeklyData.has(weekKey)) {
        weeklyData.set(weekKey, {
          weekStart: weekKey,
          weekEnd: new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0],
          adCount: 0,
          totalSpend: 0,
          totalCommission: 0,
          avgROAS: 0,
        });
      }

      const week = weeklyData.get(weekKey);
      week.adCount += 1;
      week.totalSpend += parseNum(ad["Ad Spend"]);
      week.totalCommission += parseNum(ad.Commission);
    });

    return Array.from(weeklyData.values())
      .map((week) => ({
        ...week,
        avgROAS:
          week.totalSpend > 0
            ? (week.totalCommission / week.totalSpend) * 20 // Assuming 5% commission
            : 0,
        amount: week.totalCommission,
        status: "available",
      }))
      .sort((a, b) => new Date(b.weekStart) - new Date(a.weekStart));
  }, [processedAffiliateData]);

  const totalAvailable = useMemo(
    () => weeklyEarnings.reduce((sum, week) => sum + week.amount, 0),
    [weeklyEarnings]
  );

  const selectedAmount = useMemo(() => {
    return weeklyEarnings
      .filter((week) => selectedWeeks.has(week.weekStart))
      .reduce((sum, week) => sum + week.amount, 0);
  }, [selectedWeeks, weeklyEarnings]);

  const handleWeekToggle = (weekStart) => {
    setSelectedWeeks((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(weekStart)) {
        newSelected.delete(weekStart);
      } else {
        newSelected.add(weekStart);
      }
      return newSelected;
    });
  };

  const handleSubmit = async () => {
    if (selectedWeeks.size === 0 || !paypalEmail) return;

    setIsSubmitting(true);
    const requestData = {
      affiliateName,
      paypalEmail,
      weeks: Array.from(selectedWeeks),
      totalAmount: selectedAmount,
    };
    try {
      await onSubmitRequest(requestData);
      setSelectedWeeks(new Set());
      setPaypalEmail("");
      setIsExpanded(false);
    } catch (error) {
      console.error("Submission failed", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatWeekRange = (start, end) => {
    if (!isValidDate(start) || !isValidDate(end)) return "Invalid Date Range";
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${startDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })} - ${endDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })}`;
  };

  if (!isExpanded) {
    return (
      <Card className="p-4 bg-gradient-to-br from-gray-900 via-black to-gray-900 border border-white/10 shadow-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-3 bg-[#B8460E]/20 rounded-lg mr-4">
              <CreditCard className="h-6 w-6 text-[#B8460E]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                Payout Request
              </h3>
              <p className="text-sm text-gray-400">
                Request payment for your earnings
              </p>
            </div>
          </div>
          <Button onClick={() => setIsExpanded(true)}>Request Payout</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-gray-900 via-black to-gray-900 border border-white/10 shadow-2xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="p-3 bg-[#B8460E]/20 rounded-lg mr-4">
            <CreditCard className="h-6 w-6 text-[#B8460E]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Payout Request</h3>
            <p className="text-sm text-gray-400">
              Select weeks to include in your payout
            </p>
          </div>
        </div>
        <Button variant="secondary" onClick={() => setIsExpanded(false)}>
          Hide Details
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
          <p className="text-sm text-gray-400 mb-1">TOTAL AVAILABLE</p>
          <p className="text-2xl font-bold text-[#B8460E]">
            {formatCurrency(totalAvailable)}
          </p>
        </div>
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
          <p className="text-sm text-gray-400 mb-1">SELECTED AMOUNT</p>
          <p className="text-2xl font-bold text-white">
            {formatCurrency(selectedAmount)}
          </p>
        </div>
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
          <p className="text-sm text-gray-400 mb-1">WEEKS SELECTED</p>
          <p className="text-2xl font-bold text-white">{selectedWeeks.size}</p>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <h4 className="text-md font-semibold text-white">
          Weekly Earnings History
        </h4>
        <div className="max-h-64 overflow-y-auto pr-2 space-y-2">
          {weeklyEarnings.map((week) => (
            <div
              key={week.weekStart}
              onClick={() => handleWeekToggle(week.weekStart)}
              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                selectedWeeks.has(week.weekStart)
                  ? "bg-[#B8460E]/30 border border-[#B8460E]"
                  : "bg-white/5 border border-transparent hover:border-white/20"
              }`}
            >
              <div className="flex items-center">
                <div
                  className={`h-5 w-5 rounded-md border-2 mr-4 flex items-center justify-center ${
                    selectedWeeks.has(week.weekStart)
                      ? "bg-[#B8460E] border-[#B8460E]"
                      : "border-gray-500"
                  }`}
                >
                  {selectedWeeks.has(week.weekStart) && (
                    <CheckCircle className="h-4 w-4 text-white" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-white">
                    {formatWeekRange(week.weekStart, week.weekEnd)}
                  </p>
                  <p className="text-xs text-gray-400">
                    {week.adCount} ads • {formatCurrency(week.totalSpend)} spent
                    • {week.avgROAS.toFixed(2)} Avg ROAS
                  </p>
                </div>
              </div>
              <p className="text-lg font-semibold text-green-400">
                {formatCurrency(week.amount)}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-white/10 pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              PayPal Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                placeholder="Enter your PayPal email"
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-[#B8460E]"
              />
            </div>
          </div>
          <div className="flex flex-col items-end">
            <div className="text-right mb-2">
              <span className="text-gray-400">Total Request: </span>
              <span className="text-xl font-bold text-[#B8460E]">
                {formatCurrency(selectedAmount)}
              </span>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={
                isSubmitting || selectedWeeks.size === 0 || !paypalEmail
              }
              className="w-full md:w-auto"
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
              <Send className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PayoutRequest;
